import React, { useState, useEffect } from 'react';
import ServerCard from './ServerCard';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { serverAPI } from '../utils/api';

interface LibraryServer {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  rating: number;
  status: 'online' | 'offline' | 'maintenance' | 'starting' | 'starting_soon';
  bannerUrl?: string;
}

const Library: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [favoriteServers, setFavoriteServers] = useState<LibraryServer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const response = await serverAPI.getFavorites();
        const mapped: LibraryServer[] = response.data.map((s) => ({
          id: s.id,
          name: s.name,
          description: s.description,
          createdAt: s.createdAt,
          rating: s.ratingAverage || 0,
          status: s.status as 'online' | 'offline' | 'maintenance' | 'starting' | 'starting_soon',
          bannerUrl: s.bannerUrl,
        }));
        setFavoriteServers(mapped);
      } catch (error) {
        console.error('Failed to load favorites', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, []);

  const handleLaunch = (serverId: string) => {
    // In a real app, this would launch the server client
    console.log(`Launching server ${serverId}`);
    alert(`Launching server ${serverId} - In a real app, this would open the server client`);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-16 text-center">
        <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-6"></div>
        <p className="text-2xl text-gray-600 dark:text-gray-300">{t('library.loading')}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10 px-4">
      <h1 className="text-4xl font-bold mb-8 text-center bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
        {t('library.title')}
      </h1>

      {favoriteServers.length > 0 ? (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {favoriteServers.map(server => (
              <ServerCard
                key={server.id}
                id={server.id}
                name={server.name}
                description={server.description}
                createdAt={server.createdAt}
                rating={server.rating}
                status={server.status}
                initialIsFavorite={true}
                banner={server.bannerUrl}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-2xl p-12 text-center border border-gray-200 dark:border-gray-700 shadow-xl max-w-3xl mx-auto">
          <div className="text-6xl mb-6">📚</div>
          <h3 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-4">{t('library.empty_title')}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            {t('library.empty_desc')}
          </p>
          <div className="bg-gray-100 dark:bg-gray-700/50 rounded-xl p-4 max-w-md mx-auto">
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              {t('library.tip')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Library;